import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// Auth import removed - no longer needed in this component
import {
  Container, Box, Typography, TextField, Button, Paper,
  Alert, CircularProgress, Link as MuiLink, Fade, Zoom,
  IconButton, InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import SocialSignIn from '../components/auth/SocialSignIn';
import { gaEvent } from '../utils/analytics';

// Enhanced styled components with glassmorphism
const GlassContainer = styled(Container)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(2),
  background: 'transparent',
}));

const GlassPaper = styled(Paper)(({ theme }) => {
  const dark = theme.palette.mode === 'dark';
  return {
    background: dark ? 'rgba(28, 28, 45, 0.92)' : 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(25px)',
    border: dark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255, 255, 255, 0.3)',
    borderRadius: '24px',
    boxShadow: dark
      ? '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)'
      : '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.2)',
    padding: theme.spacing(5),
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',

    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '50%',
      background: dark
        ? 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0) 100%)'
        : 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 100%)',
      borderRadius: '24px 24px 0 0',
      pointerEvents: 'none',
    },

    '&:hover': {
      background: dark ? 'rgba(28, 28, 45, 0.98)' : 'rgba(255, 255, 255, 0.95)',
      boxShadow: dark
        ? '0 25px 80px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.08)'
        : '0 25px 80px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.3)',
      transform: 'translateY(-4px)',
      '&::before': { opacity: 0.7 },
    },
  };
});

const EnhancedTextField = styled(TextField)(({ theme }) => {
  const dark = theme.palette.mode === 'dark';
  return {
  '& .MuiOutlinedInput-root': {
    background: dark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(255, 255, 255, 0.7)',
    backdropFilter: 'blur(12px)',
    borderRadius: '16px',
    border: dark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(255, 255, 255, 0.3)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fontSize: '1.1rem',

    [theme.breakpoints.down('md')]: {
      backdropFilter: 'blur(8px)',
      background: dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.85)',
      borderRadius: '12px',
      fontSize: '1rem',
      '&:hover': {
        transform: 'none',
        background: dark ? 'rgba(255, 255, 255, 0.13)' : 'rgba(255, 255, 255, 0.9)',
        boxShadow: '0 4px 15px rgba(25, 118, 210, 0.1)',
      },
      '&.Mui-focused': {
        transform: 'none',
        background: dark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(255, 255, 255, 0.95)',
        boxShadow: '0 6px 20px rgba(25, 118, 210, 0.15), 0 0 0 2px rgba(25, 118, 210, 0.15)',
      },
    },

    '&:hover': {
      background: dark ? 'rgba(255, 255, 255, 0.11)' : 'rgba(255, 255, 255, 0.85)',
      borderColor: 'rgba(25, 118, 210, 0.3)',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(25, 118, 210, 0.15)',
    },

    '&.Mui-focused': {
      background: dark ? 'rgba(255, 255, 255, 0.13)' : 'rgba(255, 255, 255, 0.95)',
      borderColor: theme.palette.primary.main,
      transform: 'translateY(-4px) scale(1.02)',
      boxShadow: '0 12px 40px rgba(25, 118, 210, 0.25), 0 0 0 2px rgba(25, 118, 210, 0.15)',
    },

    '@media (hover: none)': {
      '&:hover': {
        background: dark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(255, 255, 255, 0.7)',
        transform: 'none',
        boxShadow: 'none',
      },
    },

    '& fieldset': {
      borderColor: dark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.23)',
      borderWidth: '1px',
    },

    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },

    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
    },
  },
  
  '& .MuiInputLabel-root': {
    fontSize: '1.1rem',
    fontWeight: 500,
    
    [theme.breakpoints.down('md')]: {
      fontSize: '1rem',
    },
    
    '&.Mui-focused': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    }
  },
  
  '& .MuiOutlinedInput-input': {
    padding: '16px 20px',
    fontSize: '1.1rem',

    [theme.breakpoints.down('md')]: {
      padding: '14px 16px',
      fontSize: '1rem',
    },
  },
  };
});

const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(25, 118, 210, 0); }
  100% { box-shadow: 0 0 0 0 rgba(25, 118, 210, 0); }
`;

const shimmerAnimation = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const EnhancedButton = styled(Button)(({ theme }) => ({
  borderRadius: '16px',
  padding: '16px 32px',
  fontSize: '1.1rem',
  fontWeight: 600,
  minHeight: '56px', // Better touch target
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
  boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
  border: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  // Mobile optimizations
  [theme.breakpoints.down('md')]: {
    borderRadius: '12px',
    padding: '14px 28px',
    fontSize: '1rem',
    minHeight: '48px',
    boxShadow: '0 4px 15px rgba(25, 118, 210, 0.25)',
    
    // Disable shimmer on mobile for better performance
    '&::before': {
      display: 'none',
    },
    
    '&:hover': {
      transform: 'none',
      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
      boxShadow: '0 4px 15px rgba(25, 118, 210, 0.25)',
    }
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-200px',
    width: '200px',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
    animation: `${shimmerAnimation} 2s infinite`,
  },
  
  '&:hover': {
    background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
    boxShadow: '0 12px 40px rgba(25, 118, 210, 0.4)',
    transform: 'translateY(-3px) scale(1.02)',
    
    '&::before': {
      animation: `${shimmerAnimation} 1s infinite`,
    }
  },
  
  '&:active': {
    transform: 'translateY(-1px) scale(1.01)',
    animation: `${pulseAnimation} 0.3s ease`,
  },
  
  // Touch feedback for mobile
  '@media (hover: none)': {
    '&:hover': {
      background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
      boxShadow: '0 8px 25px rgba(25, 118, 210, 0.3)',
      transform: 'none',
    },
    
    '&:active': {
      background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
      transform: 'scale(0.98)',
      boxShadow: '0 4px 15px rgba(25, 118, 210, 0.4)',
    }
  },
  
  '&:disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
    boxShadow: 'none',
    transform: 'none',
    
    '&::before': {
      display: 'none',
    }
  }
}));

const EnhancedAlert = styled(Alert)(({ theme }) => ({
  borderRadius: '16px',
  background: ({ severity }) => {
    const backgrounds = {
      error: 'rgba(244, 67, 54, 0.1)',
      warning: 'rgba(255, 152, 0, 0.1)',
      info: 'rgba(33, 150, 243, 0.1)',
      success: 'rgba(76, 175, 80, 0.1)',
    };
    return backgrounds[severity] || backgrounds.info;
  },
  backdropFilter: 'blur(12px)',
  border: ({ severity }) => {
    const borders = {
      error: '1px solid rgba(244, 67, 54, 0.3)',
      warning: '1px solid rgba(255, 152, 0, 0.3)',
      info: '1px solid rgba(33, 150, 243, 0.3)',
      success: '1px solid rgba(76, 175, 80, 0.3)',
    };
    return borders[severity] || borders.info;
  },
  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)',
  }
}));

const GlassTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  textAlign: 'center',
  marginBottom: theme.spacing(3),
  position: 'relative',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-8px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60px',
    height: '3px',
    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    borderRadius: '2px',
  }
}));

const LoadingSpinner = styled(CircularProgress)(({ theme }) => ({
  color: 'white',
  position: 'relative',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '40px',
    height: '40px',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '50%',
    animation: `${pulseAnimation} 1.5s infinite`,
  }
}));

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isNewPasswordRequired, setIsNewPasswordRequired] = useState(false);
  const [tempUser, setTempUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSignupSuccess, setShowSignupSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { signIn, completeNewPassword, user, activeRole, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect authenticated users: to dashboard if they have a role, to role selection if they don't
  useEffect(() => {
    if (user && activeRole) {
      const stored = sessionStorage.getItem('infhire_post_login_redirect');
      const params = new URLSearchParams(location.search);
      const urlReturnTo = params.get('returnTo');
      const destination = stored || urlReturnTo;

      if (destination && activeRole === 'brand') {
        sessionStorage.removeItem('infhire_post_login_redirect');
        navigate(destination, { replace: true });
      } else {
        navigate(`/${activeRole}`, { replace: true });
      }
    } else if (user && !activeRole) {
      // Google OAuth user — check if they've chosen a username yet
      const isGoogleUser = user.username?.startsWith('google_');
      const hasPreferredUsername = !!user.attributes?.preferred_username;
      if (isGoogleUser && !hasPreferredUsername) {
        // New Google user — always show the completion screen.
        // We no longer use a sessionStorage flag because the OAuth redirect
        // crosses subdomain boundaries (infhire.com → www.infhire.com), giving
        // each origin its own isolated sessionStorage, so the flag written on
        // one side was never visible on the other. Any Google user without a
        // preferred_username is definitionally new/incomplete.
        sessionStorage.removeItem('infhire_google_signup'); // clean up any stale flag
        setShowSignupSuccess(true);
      } else {
        navigate('/add-role', { replace: true });
      }
    }
  }, [user, activeRole, navigate, location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const result = await signIn(username, password);

      if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setIsNewPasswordRequired(true);
        setTempUser(result.user);
        return;
      }
      gaEvent('login', { method: 'email' });
      // Success: AuthContext sets user → useEffect above handles redirect
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewPasswordSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      setSubmitting(false);
      return;
    }

    try {
      await completeNewPassword(tempUser, newPassword);
      // Success: AuthContext sets user → useEffect above handles redirect
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Contextual message shown while auth is initializing or redirect is pending
  const isGoogleUser = user?.username?.startsWith('google_');
  const hasPreferredUsername = !!user?.attributes?.preferred_username;
  let statusMessage = 'Please wait\u2026';
  let statusSubtext = '';
  if (user && !activeRole && isGoogleUser && !hasPreferredUsername) {
    statusMessage = 'Account created!';
    statusSubtext = 'Setting up your profile\u2026';
  } else if (user && !activeRole) {
    statusMessage = 'Almost there\u2026';
    statusSubtext = 'Setting up your account\u2026';
  } else if (user && activeRole) {
    statusMessage = 'Signing you in\u2026';
  }

  // Show the sign-up success confirmation screen for new Google users arriving from /register.
  // The useEffect sets this state and clears the sessionStorage flag instead of auto-redirecting,
  // so the user can read what happened and take an explicit action to continue.
  if (showSignupSuccess) {
    return (
      <GlassContainer maxWidth="sm">
        <Fade in timeout={600}>
          <Box>
            <Zoom in timeout={400}>
              <GlassPaper elevation={0}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h2" sx={{ mb: 1 }}>🎉</Typography>
                  <GlassTypography component="h1" variant="h4">
                    You&apos;re signed up!
                  </GlassTypography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
                    Your Google account has been connected to InfHire.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    To finish setting up your account:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    1. Choose a username
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    2. Select your role — Brand or Influencer
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    3. You&apos;ll land on your dashboard
                  </Typography>
                </Box>

                <EnhancedButton
                  fullWidth
                  variant="contained"
                  onClick={() => navigate('/choose-username')}
                >
                  Complete my profile &rarr;
                </EnhancedButton>
              </GlassPaper>
            </Zoom>
          </Box>
        </Fade>
      </GlassContainer>
    );
  }

  // Show a full-screen status instead of the form when:
  // 1. Auth is initializing and user hasn't submitted a form (covers OAuth callback + initial load)
  // 2. User is already authenticated and redirect is pending (useEffect fires next render)
  if ((authLoading && !submitting) || user) {
    return (
      <GlassContainer maxWidth="sm">
        <Fade in timeout={400}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={48} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {statusMessage}
              </Typography>
              {statusSubtext && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {statusSubtext}
                </Typography>
              )}
            </Box>
          </Box>
        </Fade>
      </GlassContainer>
    );
  }

  return (
    <GlassContainer maxWidth="sm">
      <Fade in timeout={800}>
        <Box>
          <Zoom in timeout={600}>
            <GlassPaper elevation={0}>
              <GlassTypography component="h1" variant="h4">
                {isNewPasswordRequired ? 'Set New Password' : 'Sign In'}
              </GlassTypography>

              {error && (
                <Fade in timeout={400}>
                  <EnhancedAlert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </EnhancedAlert>
                </Fade>
              )}

              {location.state?.message && (
                <Fade in timeout={400}>
                  <EnhancedAlert severity="success" sx={{ mb: 3 }}>
                    {location.state.message}
                  </EnhancedAlert>
                </Fade>
              )}

              {!isNewPasswordRequired ? (
                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                  <Fade in timeout={800} style={{ transitionDelay: '200ms' }}>
                    <Box>
                      <EnhancedTextField
                        margin="normal"
                        required
                        fullWidth
                        id="username"
                        label="Username"
                        name="username"
                        autoComplete="username"
                        autoFocus
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        sx={{ mb: 3 }}
                      />
                    </Box>
                  </Fade>

                  <Fade in timeout={800} style={{ transitionDelay: '400ms' }}>
                    <Box>
                      <EnhancedTextField
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        id="password"
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        sx={{ mb: 4 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                sx={{ color: 'text.secondary' }}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Fade>

                  <Fade in timeout={800} style={{ transitionDelay: '600ms' }}>
                    <Box>
                      <EnhancedButton
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={submitting}
                        sx={{ mb: 3 }}
                      >
                        {submitting ? <LoadingSpinner size={24} /> : 'Sign In'}
                      </EnhancedButton>
                    </Box>
                  </Fade>

                  {/* Add social sign-in component */}
                  <Fade in timeout={800} style={{ transitionDelay: '800ms' }}>
                    <Box sx={{ mb: 3 }}>
                      <SocialSignIn />
                    </Box>
                  </Fade>

                  <Fade in timeout={800} style={{ transitionDelay: '1000ms' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <MuiLink
                        component={Link}
                        to="/register"
                        variant="body1"
                        sx={{
                          textDecoration: 'none',
                          color: 'primary.main',
                          fontWeight: 500,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            color: 'primary.dark',
                            textDecoration: 'underline',
                          }
                        }}
                      >
                        {"Don't have an account? Sign Up"}
                      </MuiLink>
                    </Box>
                  </Fade>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleNewPasswordSubmit} sx={{ mt: 2 }}>
                  <Fade in timeout={600}>
                    <Box>
                      <EnhancedAlert severity="info" sx={{ mb: 3 }}>
                        You need to set a new password to continue.
                      </EnhancedAlert>
                    </Box>
                  </Fade>

                  <Fade in timeout={800} style={{ transitionDelay: '200ms' }}>
                    <Box>
                      <EnhancedTextField
                        margin="normal"
                        required
                        fullWidth
                        name="newPassword"
                        label="New Password"
                        type={showNewPassword ? "text" : "password"}
                        id="newPassword"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        sx={{ mb: 3 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle new password visibility"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                edge="end"
                                sx={{ color: 'text.secondary' }}
                              >
                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Fade>

                  <Fade in timeout={800} style={{ transitionDelay: '400ms' }}>
                    <Box>
                      <EnhancedTextField
                        margin="normal"
                        required
                        fullWidth
                        name="confirmPassword"
                        label="Confirm New Password"
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        sx={{ mb: 4 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle confirm password visibility"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                edge="end"
                                sx={{ color: 'text.secondary' }}
                              >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Fade>

                  <Fade in timeout={800} style={{ transitionDelay: '600ms' }}>
                    <Box>
                      <EnhancedButton
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={submitting}
                      >
                        {submitting ? <LoadingSpinner size={24} /> : 'Set New Password'}
                      </EnhancedButton>
                    </Box>
                  </Fade>
                </Box>
              )}
            </GlassPaper>
          </Zoom>
        </Box>
      </Fade>
    </GlassContainer>
  );
};

export default Login;
